import {
    AccessFunctionality, AccessFunctionalityAddOn, AccessModule, AccessProfile, AccessType, ActionType, LastFinancialPeriodType, NetworkActionPrefix, RequestStatus,
    SESSION_EXPIRATION_TIMEOUT, SystemLanguage, WebSocketStatus
} from '../utils/constants';
import defaults from '../api/defaults';
import { deepAssign } from '../utils';
import {
    assignmentReducer, combineReducers, customExpirationNetworkObjectLoadReducer, networkActionReducer, networkEntityAddReducer, networkEntityDeleteReducer, networkEntityEditReducer,
    networkEntityLoadReducer, networkObjectLoadReducer, networkObjectVoidReducer, pipeReducers, synchronizableEnumReducer
} from '../utils/reducerFactory';
import { isInternalID } from '../api/utils';

function index(state, action, id) {
    return id !== undefined ? (id = state.getIndexByID(id)) !== -1 ? id : state.length : undefined;
}

export default combineReducers({
    webSocketStatus: (state, action) => action.type === ActionType.SET_WEB_SOCKET_STATUS ? action.status : state || WebSocketStatus.UNINITIALIZED,
    messages: customExpirationNetworkObjectLoadReducer(NetworkActionPrefix.LOAD_MESSAGES_PREFIX, defaults.NetworkObject, SESSION_EXPIRATION_TIMEOUT),
    accessProfiles: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ACCESS_PROFILES_PREFIX, defaults.NetworkArray, AccessProfile, 'AccessProfile'),
    accessModules: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ACCESS_MODULES_PREFIX, defaults.NetworkArray, AccessModule, 'AccessModule'),
    accessFunctionalities: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ACCESS_FUNCTIONALITIES_PREFIX, defaults.NetworkArray, AccessFunctionality, 'AccessFunctionality'),
    accessFunctionalityAddOns: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_ACCESS_FUNCTIONALITY_ADD_ONS_PREFIX, defaults.NetworkArray, AccessFunctionalityAddOn, 'AccessFunctionalityAddOn'),
    accessTypes: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ACCESS_TYPES_PREFIX, defaults.NetworkArray, AccessType, 'AccessType'),
    systemLanguages: synchronizableEnumReducer(NetworkActionPrefix.LOAD_SYSTEM_LANGUAGES_PREFIX, defaults.NetworkArray, SystemLanguage, 'SystemLanguage'),
    session: pipeReducers(
        networkEntityLoadReducer(NetworkActionPrefix.LOAD_SESSION_PREFIX, defaults.SessionInformation),
        combineReducers({
            FunctionalityAccesses: assignmentReducer(ActionType.LOAD_CURRENT_USER_FUNCTIONALITY_ACCESSES_SUCCESS, [], 'response')
        }),
        (state, action) => {
            state = state || defaults.SessionInformation;

            switch (action.type) {
                case ActionType.VOID_SESSION:
                    return deepAssign(state, { Custom: { valid: false } });

                case ActionType.LINK_PENDING_TENANT_FOR_MICROSOFT_TEAMS_BOT_LINKED_CONVERSATIONS_SUCCESS:
                    return deepAssign(state, {
                        HasPendingMicrosoftTeamsTenant: false,
                        HasLinkedMicrosoftTeamsTenant: true
                    });

                case ActionType.UNLINK_AND_PEND_TENANT_FOR_MICROSOFT_TEAMS_BOT_LINKED_CONVERSATIONS_SUCCESS:
                    return deepAssign(state, {
                        HasPendingMicrosoftTeamsTenant: true,
                        HasLinkedMicrosoftTeamsTenant: false
                    });

                case ActionType.SET_CURRECNT_COMPANY_AS_DEFAULT_LOGIN_COMPANY_SUCCESS:
                    return deepAssign(state, { DefaultLoginCompanyID: state.CurrentCompanyID });

                case ActionType.LOAD_DASHBOARD_PAGE_HOME_SETTINGS_SUCCESS:
                case ActionType.ADD_DASHBOARD_PAGE_HOME_SETTINGS:
                case ActionType.EDIT_DASHBOARD_PAGE_HOME_SETTINGS:
                case ActionType.DELETE_DASHBOARD_PAGE_HOME_SETTINGS_SUCCESS:
                    let settings = {};

                    switch (action.type) {
                        case ActionType.LOAD_DASHBOARD_PAGE_HOME_SETTINGS_SUCCESS: settings = action.response; break;
                        case ActionType.ADD_DASHBOARD_PAGE_HOME_SETTINGS: settings = action.dashboardPageHomeSettings; break;
                        case ActionType.EDIT_DASHBOARD_PAGE_HOME_SETTINGS: settings = action.dashboardPageHomeSettings; break;
                        case ActionType.DELETE_DASHBOARD_PAGE_HOME_SETTINGS_SUCCESS: settings = { UserID: state.UserID }; break;
                        default: break;
                    }

                    if (
                        !settings.FunctionID && !settings.UserID ||
                        settings.FunctionID === state.UserFunctionID ||
                        settings.UserID === state.UserID
                    ) {
                        return deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED } }); // we can't know here if this overrides current home page (e.g. there's personal one, but we change one for teams), so let's reload
                    }

                    return state;

                case ActionType.DELETE_DASHBOARD_PAGE_SUCCESS:
                    if (action.pageID === state.HomePageID) {
                        return deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED }, HomePageID: null });
                    }

                    return state;

                case ActionType.ADD_COMPANY_ACCESS_FUNCTIONALITY_ADD_ON_SUCCESS:
                    return deepAssign(state, { FunctionalityAddOns: [...state.FunctionalityAddOns, action.companyAccessFunctionalityAddOn.AddOnID].unique() });

                case ActionType.DELETE_COMPANY_ACCESS_FUNCTIONALITY_ADD_ON_SUCCESS:
                    return deepAssign(state, { FunctionalityAddOns: state.FunctionalityAddOns.filter(addOnID => addOnID !== action.accessFunctionalityAddOnID) });

                case 'SET_COMPANY_SUBSCRIPTION_LEVEL':
                case 'DISABLE_COMPANY_SUBSCRIPTION_LEVEL':
                case 'ENABLE_COMPANY_ACCESS_MODULE':
                case 'DISABLE_COMPANY_ACCESS_MODULE':
                case 'TOGGLE_SMART_STRATEGY_ACCESS_FUNCTIONALITY':
                    if (action.payload.status === RequestStatus.SUCCEEDED) {
                        return deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED } });
                    }

                    return state;

                case ActionType.EDIT_GENERAL_SETTINGS_SUCCESS:
                    return deepAssign(state, {
                        StrategyOnboardingDone: action.generalSettings.StrategyOnboardingDone,
                        StrategyExecutionTypeID: action.generalSettings.StrategyExecutionTypeID,
                        StrategyPublishingLevelID: action.generalSettings.StrategyPublishingLevelID,
                        StrategyAllowNotAlignedBreakthrough: action.generalSettings.StrategyAllowNotAlignedBreakthrough,
                        StrategyAllowUserInviteTypeID: action.generalSettings.StrategyAllowUserInviteTypeID
                    });

                case 'SET_WORKSHOP_LANGUAGE_CODE':
                case 'CLEAR_WORKSHOP_LANGUAGE_CODE':
                    return deepAssign(state, { Custom: { status: action.payload.status } });

                default:
                    return state;
            }
        }
    ),
    sessionChangeState: (state, action) => {
        const nextState = state || RequestStatus.UNSYNCED;

        if (action.type === ActionType.CHANGE_SESSION_TO) {
            return action.status;
        }

        return nextState;
    },
    sessionReversionState: (state, action) => {
        const nextState = state || RequestStatus.UNSYNCED;

        if (action.type === ActionType.REVERT_SESSION_BACK) {
            return action.status;
        }

        return nextState;
    },
    lastFinancialPeriodTypes: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_LAST_FINANCIAL_PERIOD_TYPES_PREFIX, defaults.NetworkArray, LastFinancialPeriodType, 'LastFinancialPeriodType'),
    generalSettings: pipeReducers(
        networkEntityLoadReducer(NetworkActionPrefix.LOAD_GENERAL_SETTINGS_PREFIX, defaults.GeneralSettings),
        networkEntityEditReducer(NetworkActionPrefix.EDIT_GENERAL_SETTINGS_PREFIX, defaults.GeneralSettings, (state, action) => action.generalSettings),
        (state, action) => {
            switch (true) {
                case action.type === ActionType.VOID_GENERAL_SETTINGS:
                    return action.immediately === false ? deepAssign(state, { Custom: { expires: new Date() } }) : defaults.GeneralSettings;

                case action.type === 'CALCULATE_PERFORMANCE_INDICATOR_DATA' && action.payload.status === RequestStatus.SUCCEEDED:
                    return deepAssign(state, { IndicatorCalculationRunning: true });

                case action.type === 'CALCULATE_FINANCIAL_DATA' && action.payload.status === RequestStatus.SUCCEEDED:
                    return deepAssign(state, { IndicatorValidationRunning: true });

                default:
                    return state;
            }
        }
    ),
    users: pipeReducers(
        customExpirationNetworkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_USERS_PREFIX, defaults.NetworkArray, SESSION_EXPIRATION_TIMEOUT),
        combineReducers({
            Data: pipeReducers(
                combineReducers({
                    '@userIndex': pipeReducers(
                        networkEntityLoadReducer(NetworkActionPrefix.LOAD_USER_PREFIX),
                        networkEntityAddReducer(NetworkActionPrefix.ADD_USER_PREFIX, null, (state, action) => action.user),
                        networkActionReducer(NetworkActionPrefix.SEND_USER_INVITATION_PREFIX),
                        networkActionReducer(NetworkActionPrefix.CREATE_USER_LOGIN_LINK_PREFIX),
                        (state, action) => {
                            switch (action.type) {
                                case ActionType.EDIT_USER_PENDING:
                                    return deepAssign(state, { Custom: { status: RequestStatus.PENDING } });

                                case ActionType.EDIT_USER_SUCCESS:
                                    return action.response ? deepAssign(state, { ...action.user, Custom: { status: RequestStatus.SUCCEEDED } }) : deepAssign(state, { Custom: { status: RequestStatus.FAILED } });

                                case ActionType.CREATE_USER_LOGIN_LINK_SUCCESS:
                                    return deepAssign(state, { Custom: { loginURI: action.response || '' } });

                                default:
                                    return state;
                            }
                        }
                    )
                }, { userIndex: (state, action) => index(state, action, action.userID) }),
                (state, action) => {
                    switch (action.type) {
                        case ActionType.DELETE_COMPANY_FUNCTION_SUCCESS:
                            return deepAssign(state, state.map(user => user.CompanyFunctionID === action.companyFunctionID ? { ...user, CompanyFunctionID: null } : user), true);

                        case ActionType.DROP_PENDING_USERS:
                            return deepAssign(state, state.filter(user => !isInternalID(user.ID)));

                        case ActionType.SEND_USER_INVITATION_PENDING:
                            return deepAssign(state, state.map(user => user.ID === action.receiverUserID ? { ...user, Custom: { invitationStatus: RequestStatus.PENDING } } : user), true);

                        case ActionType.SEND_USER_INVITATION_SUCCESS:
                            return deepAssign(state, state.map(user => user.ID === action.receiverUserID ? { ...user, Custom: { invitationStatus: RequestStatus.SUCCEEDED } } : user), true);

                        case ActionType.SEND_USER_INVITATION_FAIL:
                            return deepAssign(state, state.map(user => user.ID === action.receiverUserID ? { ...user, Custom: { invitationStatus: RequestStatus.FAILED } } : user), true);

                        case ActionType.ADD_USER_SUCCESS:
                        case ActionType.EDIT_USER_SUCCESS:
                            return state.copy().sortBy(({ FirstName, LastName }) => (FirstName + LastName).toLowerCase());

                        default:
                            return state;
                    }
                }
            )
        }),
        networkObjectVoidReducer(ActionType.VOID_ALL_USERS)
    ),
    passwordValidationFeedback: networkObjectLoadReducer(NetworkActionPrefix.VALIDATE_USER_PASSWORD_PREFIX, defaults.NetworkArray),
    passwordSettingStatus: networkObjectLoadReducer(NetworkActionPrefix.VALIDATE_USER_PASSWORD_PREFIX, defaults.NetworkObject),
    userCompanyAccesses: pipeReducers(
        networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_USER_COMPANY_ACCESSES_PREFIX, defaults.NetworkArray),
        combineReducers({
            Data: combineReducers({
                '@accessIndex': pipeReducers(
                    networkEntityAddReducer(NetworkActionPrefix.ADD_USER_COMPANY_ACCESS_PREFIX, null, (state, action) => action.userCompanyAccess),
                    networkEntityEditReducer(NetworkActionPrefix.EDIT_USER_COMPANY_ACCESS_PREFIX, null, (state, action) => action.userCompanyAccess),
                    networkEntityDeleteReducer(NetworkActionPrefix.DELETE_USER_COMPANY_ACCESS_PREFIX)
                )
            },
            { accessIndex: (state, action) => index(state, action, action.userCompanyAccessID) }
            )
        })
    ),
    userTrackingLevelLinks: pipeReducers(
        networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_USER_TRACKING_LEVEL_LINKS_PREFIX, defaults.NetworkArray),
        combineReducers({
            Data: combineReducers({
                '@userTrackingLevelLinkIndex': pipeReducers(
                    networkEntityAddReducer(NetworkActionPrefix.ADD_USER_TRACKING_LEVEL_LINK_PREFIX, null, (state, action) => action.userTrackingLevelLink),
                    networkEntityEditReducer(NetworkActionPrefix.EDIT_USER_TRACKING_LEVEL_LINK_PREFIX, defaults.UserTrackingLevelLink, (state, action) => action.userTrackingLevelLink)
                )
            },
            { userTrackingLevelLinkIndex: (state, action) => index(state, action, action.userTrackingLevelLinkID) }
            )
        })
    ),
    currentUserCompanyAccesses: pipeReducers(
        customExpirationNetworkObjectLoadReducer(NetworkActionPrefix.LOAD_CURRENT_USER_COMPANY_ACCESSES_PREFIX, defaults.NetworkArray, SESSION_EXPIRATION_TIMEOUT),
        (state, action) => {
            switch (action.type) {
                case ActionType.EDIT_USER_COMPANY_ACCESS_SUCCESS:
                    const companyData = state.Data.find(company => company.CompanyID === action.userCompanyAccess.CompanyID);

                    if (!companyData || companyData.UserID !== action.userCompanyAccess.UserID) {
                        return state;
                    }

                    const newCompanyData = {
                        ...companyData,
                        IsUserAdministrator: action.userCompanyAccess.IsUserAdministrator,
                        ProfileID: action.userCompanyAccess.ProfileID,
                        SystemUserTypeID: action.userCompanyAccess.SystemUserTypeID
                    };

                    return {
                        ...state,
                        Data: state.Data.map(item => item.CompanyID === companyData.CompanyID ? newCompanyData : item)
                    };

                default:
                    return state;
            }
        }
    ),
    userFeedback: networkActionReducer(NetworkActionPrefix.SEND_USER_FEEDBACK_PREFIX, { ...defaults.NetworkObject, Custom: { status: RequestStatus.UNSYNCED } }),
    globalErrorDialog(state = { isVisible: false }, action) {
        switch (action.type) {
            case ActionType.FAILED_API_RESPONSE:
                return deepAssign(state, { isVisible: true, message: action.message });
            case ActionType.HIDE_GLOBAL_ERROR_DIALOG:
            case ActionType.CLONE_DASHBOARD_PAGE_FAIL:
            case ActionType.CLONE_DASHBOARD_BLOCK_FAIL:
                return deepAssign(state, { isVisible: false });
            default:
                return state;
        }
    },
    lastMasterSearchQueries: pipeReducers(
        networkObjectLoadReducer(NetworkActionPrefix.LOAD_LAST_MASTER_SEARCH_QUERIES_PREFIX, defaults.NetworkArray),
        (state, action) => action.type === ActionType.LOAD_MASTER_SEARCH_RESULTS_PENDING ?
            deepAssign(state, {
                Data: [
                    {
                        ...defaults.MasterSearchUserInput,
                        InputValue: action.value,
                        DateAdded: new Date()
                    },
                    ...state.Data.filter(({ InputValue }) => InputValue !== action.value)
                ]
            }) :
            state
    ),
    masterSearchResults: pipeReducers(
        networkEntityLoadReducer(NetworkActionPrefix.LOAD_MASTER_SEARCH_RESULTS_PREFIX, defaults.MasterSearchReport),
        (state, action) => action.type === ActionType.VOID_MASTER_SEARCH_RESULTS ? defaults.MasterSearchReport : state
    ),
    changes: (state = [], action) => deepAssign(state,
        action.type === ActionType.VOID_ALL_DATA ? [] :
        action.type === ActionType.ADD_ENTITY_CHANGE || action.type === ActionType.DROP_ENTITY_CHANGE ?
            [...state, action.change].filter(c =>
                action.change === c && action.type === ActionType.ADD_ENTITY_CHANGE ||
                    action.change.ChangeType !== c.ChangeType ||
                    action.change.EntityType !== c.EntityType ||
                    action.change.EntityID !== c.EntityID
            ) :
            state
    )
});
